import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../setupAxios";
import Badge from "./Badge";
import { useDialog } from "../context/dialogContext";
import { isoToEs } from "../fechas";
import PisoAutocomplete from "./PisoAutocomplete";
import { enviarListaConteo } from "../listaConteo";

/*
  PisoPaneles.jsx — ventanas del Panel dinámico relacionadas con un piso:
    · HistorialPisoModal   — 📜 historial de UN piso + añadir comentario.
    · HistorialGeneralModal — 📜 historial de TODOS los pisos, con filtros.
    · PendientesCopiaModal  — 📦 pisos a los que les faltan llaves.
    · FotoPiso              — 📷 foto (opcional) de la tarjeta del piso.
*/

const ICONO_HIST = {
  comentario: "💬", copia_creada: "🔑", copia_perdida: "⛔", reactivar_juego: "↩️",
  prestamo_anulado: "🚫", preparacion_cancelada: "📦", numero_asignado: "🔢", foto: "📷",
};

// Se pintan de 200 en 200 con "Ver más" (el servidor devuelve todo, sin tope).
const POR_TANDA = 200;
function LineaTiempo({ rows, conPiso }) {
  const [visibles, setVisibles] = useState(POR_TANDA);
  useEffect(() => { setVisibles(POR_TANDA); }, [rows]);
  if (!rows.length) return <div className="tl-vacio">🗂️ No hay nada registrado todavía.</div>;
  return (
    <ul className="timeline">
      {rows.slice(0, visibles).map((r) => (
        <li key={r.id} className="tl-item">
          <span className={`tl-ico t-${r.tipo}`}>{ICONO_HIST[r.tipo] || "•"}</span>
          <div className="tl-body">
            <div className="tl-top">
              <span className="tl-tipo">{r.tipo_txt}</span>
              {conPiso && <span className="tl-piso">{r.piso_id} {r.piso || ""}</span>}
              <span className="tl-meta">{isoToEs(r.fecha)}{r.usuario_nombre ? ` · ${r.usuario_nombre}` : ""}</span>
            </div>
            <div className="tl-det">{r.detalle}</div>
          </div>
        </li>
      ))}
      {rows.length > visibles && (
        <li style={{ textAlign: "center", padding: "8px 0" }}>
          <button className="btn-soft" onClick={() => setVisibles((v) => v + POR_TANDA)}>
            Ver más ({rows.length - visibles} restantes)
          </button>
        </li>
      )}
    </ul>
  );
}

// ------------------------------------------------------------ HISTORIAL DE UN PISO
export function HistorialPisoModal({ piso, onClose }) {
  const [datos, setDatos] = useState(null);
  const [texto, setTexto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const { avisar } = useDialog();

  const cargar = useCallback(() => {
    api.get(`/piso_historial/${piso.pid}`).then(({ data }) => setDatos(data)).catch(() => setDatos({ rows: [] }));
  }, [piso.pid]);
  useEffect(() => { cargar(); }, [cargar]);

  const comentar = async () => {
    if (!texto.trim()) return;
    setGuardando(true);
    try {
      const { data } = await api.post("/piso_comentario", { piso_id: piso.pid, texto: texto.trim() });
      if (data.ok) { setTexto(""); cargar(); } else avisar(data.error || "No se pudo guardar.");
    } catch (err) { avisar(err.response?.data?.error || "No se pudo guardar."); }
    finally { setGuardando(false); }
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>📜 Historial del piso</h3>
          <span className="tl-piso" style={{ fontSize: 13 }}>{piso.pid} {piso.codigo}</span>
          <span className="grow" />
          {datos?.rows && <span className="sub">{datos.rows.length} evento(s)</span>}
        </div>
        <div className="coment-box">
          <textarea rows={2} value={texto} maxLength={2000} placeholder="Añadir un comentario al piso…"
                    onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) comentar(); }} />
          <button className="btn btn-primary" onClick={comentar} disabled={guardando || !texto.trim()}>
            {guardando ? "Guardando…" : "💬 Comentar"}
          </button>
        </div>
        {!datos ? <p className="muted">Cargando…</p> : <LineaTiempo rows={datos.rows || []} />}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ HISTORIAL GENERAL
export function HistorialGeneralModal({ onClose }) {
  const [rows, setRows] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [pisoTxt, setPisoTxt] = useState("");
  const [piso, setPiso] = useState(null);
  const [tipo, setTipo] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [q, setQ] = useState("");
  const timer = useRef(null);

  const cargar = useCallback(() => {
    const params = {};
    if (piso) params.piso = piso.id_origen;
    if (tipo) params.tipo = tipo;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (q.trim()) params.q = q.trim();
    api.get("/historial_pisos", { params }).then(({ data }) => {
      setRows(data.rows || []);
      if (data.tipos) setTipos(data.tipos);
    }).catch(() => setRows([]));
  }, [piso, tipo, desde, hasta, q]);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(cargar, 250);
    return () => clearTimeout(timer.current);
  }, [cargar]);

  const limpiar = () => { setPisoTxt(""); setPiso(null); setTipo(""); setDesde(""); setHasta(""); setQ(""); };
  const hayFiltros = piso || tipo || desde || hasta || q;

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>📜 Historial general de pisos</h3>
          <span className="grow" />
          {rows && <span className="sub">{rows.length} evento(s)</span>}
        </div>
        <div className="filtros-bar">
          <div style={{ minWidth: 220 }}>
            <label>Piso</label>
            <PisoAutocomplete value={pisoTxt} placeholder="ID o abreviatura"
                              className={piso ? "input-ok" : ""}
                              onChange={(v) => { setPisoTxt(v); if (piso) setPiso(null); }}
                              onSelect={setPiso} />
          </div>
          <div>
            <label>Tipo</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">Todos</option>
              {tipos.map((t) => <option key={t.tipo} value={t.tipo}>{ICONO_HIST[t.tipo] || "•"} {t.txt}</option>)}
            </select>
          </div>
          <div>
            <label>Desde</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div>
            <label>Hasta</label>
            <input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} />
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <label>Texto</label>
            <input value={q} placeholder="Buscar en el detalle…" onChange={(e) => setQ(e.target.value)} style={{ width: "100%" }} />
          </div>
          {hayFiltros && <button className="btn-soft" onClick={limpiar}>✕ Limpiar</button>}
        </div>
        {!rows ? <p className="muted">Cargando…</p> : <LineaTiempo rows={rows} conPiso />}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ PISOS QUE NECESITAN COPIAS
const numCaj = (c) => { const n = parseInt(c, 10); return Number.isNaN(n) ? Infinity : n; };
const esc = (t) => String(t ?? "").replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch]));

// Imprime la lista en una ventana aparte (solo la tabla, sin la app alrededor).
// Solo se imprimen los pisos seleccionados (rows ya viene filtrado).
const textoSeguridad = (r) => (r.seguridad ? "Sí" : "No");
function imprimirLista(rows) {
  const totalFaltan = rows.reduce((n, r) => n + (r.faltan || 0), 0);
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) { alert("El navegador ha bloqueado la ventana de impresión: permite ventanas emergentes para esta web."); return; }
  const filas = rows.map((r) => `<tr><td>${esc(r.cajetin ?? "—")}</td><td>${esc(r.pid)}</td><td><b>${esc(r.piso)}</b></td>
    <td>${esc(r.empresa ?? "—")}</td><td class="${r.seguridad ? "seg" : ""}">${esc(textoSeguridad(r))}</td>
    <td class="n">${esc(r.plazas ?? "—")}</td><td class="n">${esc(r.necesarias)}${r.manual ? " ✏️" : ""}</td>
    <td class="n">${esc(r.reales)}</td><td class="n">${esc(r.en_cajetin)}</td><td class="n f">−${esc(r.faltan)}</td><td class="chk"></td></tr>`).join("");
  const hoy = new Date().toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" });
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Pisos que necesitan copias</title>
<style>
  body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;margin:24px}
  h1{font-size:20px;margin:0 0 4px} .sub{color:#475569;font-size:13px;margin:0 0 14px}
  table{border-collapse:collapse;width:100%;font-size:13px}
  th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left} th{background:#f1f5f9;font-size:11px;text-transform:uppercase;letter-spacing:.03em}
  td.n{text-align:right} td.f{font-weight:800;color:#b91c1c} td.chk{width:70px} td.seg{font-weight:800;color:#7c3aed}
  tr:nth-child(even) td{background:#f8fafc}
  @media print{body{margin:10mm} tr{page-break-inside:avoid}}
</style></head><body>
<h1>📦 Pisos que necesitan copias</h1>
<p class="sub">${esc(rows.length)} piso(s) · faltan <b>${esc(totalFaltan)}</b> llave(s) · ordenado por cajetín · ${esc(hoy)}</p>
<table><thead><tr><th>Cajetín</th><th>ID</th><th>Piso</th><th>Empresa</th><th>Seguridad</th><th>Plazas</th><th>Necesarias</th><th>Reales</th><th>En cajetín</th><th>Faltan</th><th>Hecho</th></tr></thead>
<tbody>${filas}</tbody></table></body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 250);
}

export function PendientesCopiaModal({ onClose, onVerHistorial }) {
  const [datos, setDatos] = useState(null);
  // pid seleccionados: se imprimen solo estos, y se cuentan los que tienen cajetín.
  const [sel, setSel] = useState(() => new Set());
  useEffect(() => {
    api.get("/pisos_pendientes_copia").then(({ data }) => {
      setDatos(data);
      setSel(new Set((data.rows || []).map((r) => r.pid)));
    }).catch(() => setDatos({ rows: [] }));
  }, []);

  const rows = datos?.rows || [];
  const porCajetin = [...rows].sort((a, b) => numCaj(a.cajetin) - numCaj(b.cajetin) || String(a.cajetin).localeCompare(String(b.cajetin)));
  const contables = porCajetin.filter((r) => r.cajetin != null);
  const nSel = contables.filter((r) => sel.has(r.pid)).length;          // para contar (con cajetín)
  const seleccionadas = porCajetin.filter((r) => sel.has(r.pid));       // para imprimir
  const toggle = (pid) => setSel((prev) => { const n = new Set(prev); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; });

  const contar = () => {
    const items = contables.filter((r) => sel.has(r.pid)).map((r) => ({ cajetin: r.cajetin, pid: r.pid, codigo: r.piso }));
    if (!items.length) return;
    enviarListaConteo("Pisos que necesitan copias", items);
    onClose();
  };

  return (
    <div className="modal-back" onMouseDown={onClose}>
      <div className="modal modal-xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>📦 Pisos que necesitan copias</h3>
          <span className="grow" />
          {datos?.rows && (
            <span className="sub">{rows.length} piso(s) · faltan <b>{datos.total_faltan}</b> llave(s)</span>
          )}
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Pisos activos con menos llaves reales que las necesarias (el objetivo manual del Panel dinámico si lo
          tienen ✏️; si no, plazas + 6, o + 5 si son AT y + 3 si son no adscrito). Ordenados por cajetín.
          Se imprimen y se cuentan solo los seleccionados.
        </p>
        {datos && rows.length > 0 && (
          <div className="pc-barra">
            <button className="btn-soft" onClick={() => setSel(new Set(porCajetin.map((r) => r.pid)))}>☑️ Marcar todos</button>
            <button className="btn-soft" onClick={() => setSel(new Set())}>⬜ Desmarcar todos</button>
            <span className="muted" style={{ fontSize: 13 }}>
              {seleccionadas.length} de {porCajetin.length} seleccionados
              {seleccionadas.length !== nSel && ` (${nSel} con cajetín para contar)`}
            </span>
            <span className="grow" />
            <button className="btn" onClick={() => imprimirLista(seleccionadas)} disabled={!seleccionadas.length}
                    title="Imprime solo los pisos seleccionados">
              🖨️ Imprimir seleccionados ({seleccionadas.length})
            </button>
            <button className="btn btn-primary" onClick={contar} disabled={!nSel}
                    title="Abre Contar cajetines con estos pisos, en orden de cajetín">
              🔢 Contar cajetines ({nSel})
            </button>
          </div>
        )}
        {!datos ? <p className="muted">Cargando…</p> : (
          <div className="tablewrap" style={{ maxHeight: "55vh", overflowY: "auto" }}>
            <table>
              <thead>
                <tr><th style={{ width: 34 }}></th><th>Cajetín</th><th>ID</th><th>Piso</th><th>Empresa</th><th>Seguridad</th><th>Plazas</th><th>Necesarias</th>
                  <th>Reales</th><th>En cajetín</th><th>Faltan</th><th></th></tr>
              </thead>
              <tbody>
                {porCajetin.map((r) => (
                  <tr key={r.pid} className={!sel.has(r.pid) ? "fila-apagada" : ""}>
                    <td>
                      <input type="checkbox" checked={sel.has(r.pid)} onChange={() => toggle(r.pid)}
                             aria-label={`Seleccionar ${r.piso}`} style={{ width: 18, height: 18 }}
                             title={r.cajetin != null ? "" : "Sin cajetín: se imprime, pero no se puede contar"} />
                    </td>
                    <td className="strong">{r.cajetin ?? <span className="muted">sin cajetín</span>}</td>
                    <td className="mono">{r.pid}</td>
                    <td className="strong">{r.piso}</td>
                    <td>{r.empresa ?? <span className="muted">—</span>}</td>
                    <td>{r.seguridad ? <Badge tone="purple">🔒 Sí</Badge> : <span className="muted">No</span>}</td>
                    <td className="num-cell">{r.plazas ?? "—"}</td>
                    <td className="num-cell">{r.necesarias}{r.manual && <span title="Objetivo manual"> ✏️</span>}</td>
                    <td className="num-cell">{r.reales}</td>
                    <td className="num-cell"><b>{r.en_cajetin}</b></td>
                    <td className="num-cell"><Badge tone={r.faltan >= 4 ? "red" : "amber"}>−{r.faltan}</Badge></td>
                    <td>
                      <button className="btn btn-mini" title="Historial del piso"
                              onClick={() => onVerHistorial?.({ pid: r.pid, codigo: r.piso })}>📜</button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={12} className="empty">🎉 Todos los pisos tienen las llaves que necesitan.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ FOTO DE LA TARJETA
// Reduce la imagen en el navegador (máx. 1600 px, JPEG) antes de subirla:
// ocupa mucho menos en el servidor y tarda menos, sin perder legibilidad.
function reducirImagen(file, maxLado = 1600) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, maxLado / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * k);
      canvas.height = Math.round(img.height * k);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("No es una imagen válida")); };
    img.src = url;
  });
}

export function FotoPiso({ pid }) {
  const [src, setSrc] = useState(null);       // blob: URL de la foto actual
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [zoom, setZoom] = useState(false);
  const inputRef = useRef(null);
  const { avisar, confirmar } = useDialog();

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await api.get(`/piso_foto/${pid}`, { responseType: "blob" });
      setSrc((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch {
      setSrc((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    } finally { setCargando(false); }
  }, [pid]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);

  const subir = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { avisar("Elige una imagen (JPG, PNG o WEBP)."); return; }
    if (src && !(await confirmar("Este piso ya tiene foto. ¿Sustituirla por la nueva?"))) return;
    setSubiendo(true);
    try {
      const data = await reducirImagen(file);
      const { data: r } = await api.post("/piso_foto", { piso_id: pid, data, nombre: file.name });
      if (r.ok) await cargar(); else avisar(r.error || "No se pudo subir la foto.");
    } catch (err) {
      avisar(err.response?.data?.error || err.message || "No se pudo subir la foto.");
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const borrar = async () => {
    if (!(await confirmar("¿Quitar la foto de la tarjeta de este piso?"))) return;
    try {
      const { data } = await api.post(`/piso_foto/${pid}/borrar`);
      if (data.ok) cargar(); else avisar(data.error || "No se pudo quitar.");
    } catch (err) { avisar(err.response?.data?.error || "No se pudo quitar."); }
  };

  return (
    <div className="foto-card">
      {src ? <img src={src} alt="Tarjeta del piso" onClick={() => setZoom(true)} title="Ver en grande" />
           : <div className="foto-vacia">{cargando ? "…" : "📷"}</div>}
      <div className="foto-acc">
        <b style={{ fontSize: 13 }}>📷 Foto de la tarjeta <span className="muted" style={{ fontWeight: 400 }}>(opcional)</span></b>
        <span className="muted" style={{ fontSize: 12 }}>
          {src ? "Haz clic en la foto para verla en grande." : "Útil para reconocer la tarjeta o el mando del portal."}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" className="btn btn-mini acc-azul" onClick={() => inputRef.current?.click()} disabled={subiendo}>
            {subiendo ? "Subiendo…" : src ? "🔄 Cambiar" : "⬆️ Subir foto"}
          </button>
          {src && <button type="button" className="btn btn-mini acc-rojo" onClick={borrar}>🗑️ Quitar</button>}
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
               onChange={(e) => subir(e.target.files?.[0])} />
      </div>
      {zoom && src && <div className="foto-zoom" onClick={() => setZoom(false)}><img src={src} alt="Tarjeta del piso" /></div>}
    </div>
  );
}
